import { ElementWithMeta } from '../../types';
import { FormControllerFieldMeta } from '../controllers/SmartformContext';
import { FieldValidatorInterface } from '../controllers/FieldValidatorInterface';
import { getModelPathFromIndex } from './model';
import { InputModel } from './model/types';
import { restoreSanitizedFieldName } from './model/fieldNameSanitizer';

export function transformCustomFieldURN(fieldUrn: string, customFieldId: string) {
  return fieldUrn.replace('extensions:<customField>', `extensions:${customFieldId}`);
}

export function preProcessElements<Model = InputModel>(
  validator: FieldValidatorInterface | undefined,
  elements: ElementWithMeta<Model>[],
  values: object,
  contextMap?: Map<string, object>,
  fieldMeta?: FormControllerFieldMeta | undefined
) {
  if (!validator || !values) {
    return elements;
  }

  const modifiedElements = elements.map((element) => {
    const newElement = { ...element };

    // If an element is hidden and has a required field, unhide it
    if (newElement.params?.hidden) {
      const fieldThatIsRequired = element.fields.find((field) => field.validation?.required?.length);
      if (fieldThatIsRequired) newElement.params = { ...newElement.params, hidden: false, readonly: true };
    }

    const indicesOfUnallowedFields = element.meta
      .map((metaEntry, index) => {
        const fieldURN = element.params?.id
          ? transformCustomFieldURN(metaEntry.urn, element.params?.id)
          : metaEntry.urn;

        const hasDisallowedRule = validator.isFieldDisallowed(fieldURN, values, contextMap);

        if (hasDisallowedRule) {
          return index;
        }

        return null;
      })
      .filter((indexOrNull) => indexOrNull !== null);

    newElement.fields = element.fields?.filter((_field, index) => !indicesOfUnallowedFields.includes(index));
    newElement.meta = element.meta?.filter((_field, index) => !indicesOfUnallowedFields.includes(index));

    const indicesOfRequiredFields = newElement.meta
      .map((metaEntry) => {
        const fieldURN = element.params?.id
          ? transformCustomFieldURN(metaEntry.urn, element.params?.id)
          : metaEntry.urn;
        return validator.isFieldRequired(fieldURN, values, contextMap);
      })
      .map((isRequiredSet, index) => {
        if (isRequiredSet === true) {
          return index;
        }

        return null;
      })
      .filter((indexOrNull) => indexOrNull !== null);

    newElement.fields = newElement.fields?.map((field, index) => {
      const name = getModelPathFromIndex(element, index);
      const fieldIsDisabled = fieldMeta?.[restoreSanitizedFieldName(name)]?.disabled;

      if (fieldMeta?.[restoreSanitizedFieldName(name)]?.permissions?.READ === false) return { ...field };

      let toReturn = { ...field };

      if (indicesOfRequiredFields.includes(index) && !fieldIsDisabled) {
        return {
          ...toReturn,
          validation: {
            ...toReturn.validation,
            required: ['This field is required']
          }
        };
      }
      if (fieldIsDisabled || element.params?.hidden) {
        toReturn = { ...toReturn, validation: undefined };

        if (newElement.meta[index]?.validation) {
          newElement.meta[index] = { ...newElement.meta[index]!, validation: undefined };
        }
      }
      return toReturn;
    });

    return newElement;
  });

  return modifiedElements.filter((element) => element.fields.length !== 0);
}
